/**
 * Default copy and safe HTML for gallery category pages.
 * Shared by the server render and the client, so this file stays CommonJS
 * and only depends on the language runtime.
 */

const DEFAULT_CATEGORY_CONTENT = {
  family: {
    seoTitle: 'Family Photographer Cape Town | Relaxed Beach Shoots',
    seoDescription: 'Family photographer in Cape Town for relaxed beach photoshoots in Noordhoek, Kommetjie and Hout Bay. Natural photos of kids, parents and grandparents.',
    h1: 'Family Photographer in Cape Town',
    intro: [
      'I photograph families the way they really are: the toddler who won\'t stand still, the teenager who secretly loves a hug, the grandparents who have waited all year to have everyone in one place. My family shoots are relaxed, outdoors and unhurried. I don\'t line you up in front of a wall and ask for twenty identical smiles. Instead, we walk, play, chat and wander along the sand, and I photograph what happens in between.',
      'I live and work at the foot of Chapman\'s Peak in Noordhoek, so the beaches and mountains of the Cape Peninsula are my everyday studio. If you want photos that look like your family on a good day, with real light, real laughter and a bit of wind in your hair, you\'re in the right place.',
    ].join('\n\n'),
    body: [
      '## What a family shoot with me is like',
      'Before the shoot, we have a quick chat about who is coming, how old the children are and what you\'d love to have on your wall afterwards. I help you choose a location and a time of day that suit your family. I\'m happy to keep an eye on the weather with you, because in Cape Town the wind decides a lot.',
      'On the day, I give gentle guidance rather than stiff poses. I\'ll suggest a walk along the water\'s edge, a race to the dunes, a cuddle on a blanket or a piggyback for the smallest legs. Children usually relax within a few minutes once they realise this is more like an outing than a photoshoot. I also make sure to get the "proper" photos for the grandparents: everyone together, looking at the camera, nobody mid-sneeze.',
      'Kids, parents and grandparents are all welcome. Whether it\'s just the three of you or three generations at once, I plan the session so that everyone gets photographed, not only the people who are easiest to pose.',
      'Afterwards, I edit your photos and deliver them in a professionally edited online gallery that you can download and share with family.',
      '## Where I photograph families',
      'Most of my family sessions happen on the beaches and trails close to home:',
      '- Noordhoek Beach, a long, wide stretch of white sand with Chapman\'s Peak at one end. There\'s plenty of room for children to run, and the light at golden hour is beautiful.',
      '- Kommetjie, with its lighthouse, rocks and tidal pools for little explorers.',
      '- Fish Hoek, a calm, family-friendly beach on the False Bay side, often a good option when the wind is up on the Atlantic side.',
      '- Hout Bay, with mountain views, the harbour and a beach that\'s easy for prams and grandparents.',
      'I\'m also happy to travel to other Cape Town beaches, forests and gardens, or to a place that means something to your family. If you\'re not sure where to go, have a look at my guide to the [best family photo locations in Cape Town](/blog/family-photo-locations-cape-town).',
      'The best light is usually in the early morning or the last hour or two before sunset. In summer, an early session is often calmer and cooler, and a lot easier on small children.',
      '## What to wear',
      'Choose clothes that you feel comfortable in and that let you move, sit on the sand and pick up a child. A few things that work well:',
      '- Coordinate, don\'t match. Choose two or three colours that go together, such as soft neutrals, muted blues, sage or warm earthy tones, rather than identical white shirts and jeans.',
      '- Avoid big logos and busy prints, which pull attention away from faces.',
      '- Think about texture and movement. Linen, knitwear and dresses that move in the breeze photograph beautifully on the beach.',
      '- Bring a layer. Cape Town evenings can turn cool and windy quickly, and a jersey or light jacket keeps everyone happy.',
      '- Bare feet are perfect on the sand. Just bring towels and a change of clothes for anyone who ends up in the water, because somebody always does.',
      'When you book, I\'ll send you more detailed guidance on outfits and planning.',
      '## Family photography packages',
      'These are my current family packages:',
      '- [Family Mini Session](/services/family-mini-session): 1 hour, R2,800',
      '- [Classic Family Portrait](/services/classic-family-portrait): 2 hours, R4,800',
      '- [Extended Family Session](/services/extended-family-session): 3 hours, R6,800',
      '- [Family Gathering](/services/family-gathering): 4 hours, R6,500 special (usually R8,800)',
      '- [Family Reunion](/services/family-reunion): 6 hours, R12,000',
      '- [Family Celebration](/services/family-celebration): 8 hours, R15,000',
      '**Family Summer Special:** R2,500 for 45 minutes. This is a relaxed outdoor family session on a beach or nature location within 20 km of Noordhoek, in golden-hour morning or evening light, with a professionally edited online gallery. Bookings and shoots run until 15 December 2026. [See the Family Summer Special](/blog/summer-special-cape-town-photoshoot).',
      'You can see everything I offer on my [services page](/services). For anything bigger or different, such as a milestone birthday or a family gathering at home, just ask and I\'ll put together a quote.',
      'Ready to plan your family shoot? [Get in touch](/contact) or call me on [082 415 1652](tel:+27824151652). The Family Summer Special runs until 15 December 2026.',
    ].join('\n\n'),
    faqs: [
      {
        question: 'Where do family photoshoots take place?',
        answer: 'Most of my sessions are on the beaches around Noordhoek, Kommetjie, Fish Hoek and Hout Bay, but I photograph families all over Cape Town. A garden or a forest trail works well too. I\'ll help you choose a spot that suits your family and the weather.',
      },
      {
        question: 'What\'s the best time of day for family photos?',
        answer: 'Early morning or the last hour or two before sunset gives the softest, most flattering light. In summer I often suggest an early session, when it\'s cooler, calmer and the beaches are quieter.',
      },
      {
        question: 'What if it\'s windy or rains on the day?',
        answer: 'Cape Town weather has a mind of its own. I watch the forecast with you in the days before the shoot. If it looks too wild, we can talk about a more sheltered location or another date.',
      },
      {
        question: 'My children are very young or won\'t sit still. Is that a problem?',
        answer: 'Not at all. I don\'t expect anyone to sit still for long. The session is built around play and movement, and some of my favourite photos happen when a toddler runs off towards the waves. We go at your children\'s pace.',
      },
      {
        question: 'Can grandparents and the extended family join?',
        answer: 'Yes. Kids, parents and grandparents are all welcome. For bigger groups I\'d suggest a longer package, such as the Classic Family Portrait or the Family Gathering, so there\'s time for everyone and for all the different groupings.',
      },
      {
        question: 'How do I book?',
        answer: 'Send me a message through the contact page or call or WhatsApp me on 082 415 1652 with your preferred dates, where you\'d like to shoot and who is coming. I\'ll confirm availability and help you with the rest.',
      },
    ],
  },
};

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function filled(value) {
  return Boolean(String(value == null ? '' : value).trim());
}

function truncateDescription(value) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= 155) return text;
  const cut = text.slice(0, 155);
  const space = cut.lastIndexOf(' ');
  return (space > 40 ? cut.slice(0, space) : cut).replace(/[\s,;:.-]+$/, '');
}

function currentDescription(source) {
  const text = String(source || '').replace(/\s+/g, ' ').trim();
  if (text.length <= 155) return text;
  return `${text.slice(0, 152).trim()}…`;
}

function pickText(stored, fallback, last) {
  if (filled(stored)) return String(stored).trim();
  if (filled(fallback)) return String(fallback).trim();
  return last;
}

function faqItems(faqs) {
  return (Array.isArray(faqs) ? faqs : [])
    .map((item) => ({
      question: String((item && item.question) || '').replace(/\s+/g, ' ').trim(),
      answer: String((item && item.answer) || '').replace(/\s+/g, ' ').trim(),
    }))
    .filter((item) => item.question && item.answer);
}

function resolveCategoryFields(category, name, slug, currentIntro) {
  const defaults = DEFAULT_CATEGORY_CONTENT[slug] || {};
  const storedFaqs = faqItems(category && category.faqs);
  const fallbackFaqs = faqItems(defaults.faqs);
  return {
    title: pickText(category && category.seoTitle, defaults.seoTitle, `${name} | Cape Town Photographer`),
    description: filled(category && category.seoDescription)
      ? truncateDescription(category.seoDescription)
      : (filled(defaults.seoDescription) ? truncateDescription(defaults.seoDescription) : currentDescription(currentIntro)),
    h1: pickText(category && category.h1, defaults.h1, name),
    intro: pickText(category && category.intro, defaults.intro, currentIntro),
    body: pickText(category && category.body, defaults.body, ''),
    faqs: storedFaqs.length ? storedFaqs : fallbackFaqs,
  };
}

function safeHref(url) {
  const raw = String(url || '').trim();
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  if (raw === 'https://carmenlorraine.com' || raw.startsWith('https://carmenlorraine.com/')) return raw;
  if (/^tel:\+?[0-9]+$/.test(raw)) return raw;
  return '';
}

function inline(text) {
  const source = String(text || '');
  const pattern = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g;
  return source.split(pattern).map((part) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return `<strong>${escapeHtml(bold[1])}</strong>`;
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const href = safeHref(link[2]);
      const label = escapeHtml(link[1]);
      if (!href) return label;
      return `<a href="${escapeHtml(href)}">${label}</a>`;
    }
    return escapeHtml(part);
  }).join('');
}

function renderMarkdown(source) {
  const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
  const html = [];
  let list = [];
  const flush = () => {
    if (!list.length) return;
    html.push(`<ul>${list.map((item) => `<li>${inline(item)}</li>`).join('')}</ul>`);
    list = [];
  };
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flush();
      return;
    }
    if (trimmed.startsWith('### ')) {
      flush();
      html.push(`<h3>${inline(trimmed.slice(4))}</h3>`);
      return;
    }
    if (trimmed.startsWith('## ')) {
      flush();
      html.push(`<h2>${inline(trimmed.slice(3))}</h2>`);
      return;
    }
    if (trimmed.startsWith('- ')) {
      list.push(trimmed.slice(2));
      return;
    }
    flush();
    html.push(`<p>${inline(trimmed)}</p>`);
  });
  flush();
  return html.join('');
}

function renderIntroHtml(intro) {
  return String(intro || '')
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${inline(paragraph)}</p>`)
    .join('');
}

function renderBodyHtml(body) {
  return renderMarkdown(body);
}

function renderFaqHtml(faqs) {
  const items = faqItems(faqs);
  if (!items.length) return '';
  return `<h2>Frequently asked questions</h2>${items.map((item) => (
    `<h3>${escapeHtml(item.question)}</h3><p>${escapeHtml(item.answer)}</p>`
  )).join('')}`;
}

function renderCategoryContentHtml(filter) {
  const page = filter || {};
  return `${renderIntroHtml(page.intro)}${renderBodyHtml(page.body)}${renderFaqHtml(page.faqs)}`;
}

function renderBodyAndFaqHtml(filter) {
  const page = filter || {};
  return `${renderBodyHtml(page.body)}${renderFaqHtml(page.faqs)}`;
}

function faqPageNode(faqs) {
  const items = faqItems(faqs);
  if (!items.length) return null;
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

function faqPageJson(faqs) {
  const node = faqPageNode(faqs);
  if (!node) return '';
  return JSON.stringify(Object.assign({ '@context': 'https://schema.org' }, node));
}

module.exports = {
  DEFAULT_CATEGORY_CONTENT,
  escapeHtml,
  truncateDescription,
  resolveCategoryFields,
  renderIntroHtml,
  renderBodyHtml,
  renderFaqHtml,
  renderCategoryContentHtml,
  renderBodyAndFaqHtml,
  faqPageNode,
  faqPageJson,
  safeHref,
};
